/* eslint-disable  @typescript-eslint/no-this-alias */
import user from './user';
import h, { getAvodConfiguration, getCountry } from './common';
import player from './bitmovinPlayer';
import url from './url';
import { SmartLib, LoggerManager, PlayerEventListener, StreamingSessionOptions } from '@broadpeak/smartlib';
import '@broadpeak/smartlib-analytics';
import '@broadpeak/smartlib-ad';
import '@broadpeak/smartlib-bitmovin';
import { PlayerAPI, PlayerEvent } from 'bitmovin-player';
import { AttachPlayerEventsCallback } from '../features/player';
import npawPlugin from '../singletons/npawPluginSingleton';
import type { ILocalStorageConfigurationObject } from '../types/ILocalStorage';
import { isApiNotAvailableError, isPlayerAlive, markPlayerDestroyed } from './playerGuards';
import { CDN, SMARTLIB_LOG_LEVEL } from './cdn/constants';
import { retryN } from './cdn/retry';
import { IAdEvents, IAdBreakData, IAdData } from './cdn/types';
import { playerSSAIManager } from '../utils/SSAI';

// import {
//     SmartLib as SmartLibType,
//     LoggerManager as LoggerManagerType,
//     PlayerEventListener as PlayerEventListenerType,
//     StreamingSessionOptions as StreamingSessionOptionsType,
// } from '@broadpeak/smartlib';

// let SmartLib: typeof SmartLibType,
//     LoggerManager: typeof LoggerManagerType,
//     PlayerEventListener: typeof PlayerEventListenerType,
//     StreamingSessionOptions: typeof StreamingSessionOptionsType;

function smartlibLogger(n: number): void {
    void n;
}

async function loadSmartLib(): Promise<void> {
    //const smartLib = await import(/* webpackChunkName: "@broadpeak/smartlib-v3" */ '@broadpeak/smartlib');
    //SmartLib = smartLib.SmartLib;
    //LoggerManager = smartLib.LoggerManager;
    // PlayerEventListener = smartLib.PlayerEventListener;
    //StreamingSessionOptions = smartLib.StreamingSessionOptions;
}

const cdnHandler = {
    config: null as any,
    avodConfig: null as ILocalStorageConfigurationObject['avodConfigs'],
    _isAdsEnabled: false,
    _player: null as PlayerAPI,
    _session: null as any,
    _initialized: false,
    _cdnName: '',
    _requestUrl: '',
    _resolvedsmartlibUrl: '',
    _cdnAdsUrlParams: [],
    _numberOfSessions: 0,
    _resolveQueue: Promise.resolve(),
    /**
     * Resolves a URL using SmartLib CDN handling
     * @param url - The original URL to resolve
     * @param token - Optional authentication token
     * @returns The resolved URL from CDN or the original URL as fallback
     */
    async _resolveUrlWithQueue(url: string, token?: string, callback?: () => void): Promise<string> {
        // Handle invalid cases
        this._resolveQueue = this._resolveQueue.then(async () => {
            if (!url) return '';
            const { retryAttempt, retryDelay, enabled } = this.config || {};
            if (!enabled) return url;

            // Ensure session exists
            // if (!this._session) {
            //     this.initiateSession();
            //     callback?.();
            // }
            if (!this.isSessionAlive(this._session)) {
                this.stopCDNSession();
                this.initiateSession();
                callback?.();
            }

            // Prepare URL with required parameters
            const parsedUrl = new URL(url);
            if (token) {
                parsedUrl.searchParams.set('token', token);
            }

            if (this._isAdsEnabled && this._cdnAdsUrlParams && this._cdnAdsUrlParams.length > 0) {
                // parsedUrl.searchParams.set('cdn', 'akm');
                // parsedUrl.searchParams.set('ssai', 'true');
                this._cdnAdsUrlParams.forEach((param) => {
                    parsedUrl.searchParams.set(param.key, param.value);
                });
            }

            if (playerSSAIManager.canRunSSAI) await playerSSAIManager.addSSAIParamsToCDNHandlerURL(parsedUrl);

            // Format URL for SmartLib
            const formattedUrl = parsedUrl.toString();
            this._requestUrl = formattedUrl;

            try {
                const resolvedURL = await (this as typeof cdnHandler)._resolveSmartLib(formattedUrl, callback);

                // Set CDN name based on resolved URL
                this._cdnName = this.getCDNName(resolvedURL);
                this._resolvedsmartlibUrl = resolvedURL;

                return resolvedURL;
            } catch (error) {
                // Log the error and return original URL as fallback
                this._resolvedsmartlibUrl = '';
                return formattedUrl;
            }
        });
        return this._resolveQueue;
    },

    async resolveTrailerUrl(url: string, token?: string, callback?: () => void): Promise<string> {
        return this._resolveUrlWithQueue(url, token, callback);
    },

    async resolveURL(url: string, token?: string, callback?: () => void): Promise<string> {
        return this._resolveUrlWithQueue(url, token, callback);
    },

    isSessionAlive(session: any): boolean {
        return !!session && !session.stopped;
    },

    setupAdvertising(adsParameters: Record<string, string>, cdnAdsUrlParams: Record<string, string>[]): void {
        if (!this._session) {
            return;
        }

        if (this._session?.stopped) {
            return;
        }

        this._isAdsEnabled = true;

        this._session.activateAdvertising();

        this._cdnAdsUrlParams = cdnAdsUrlParams;

        Object.entries(adsParameters).forEach(([key, value]) => {
            this._session.setAdParameter(key, value);
        });
    },

    setupEventsForSSAI(): void {
        cdnHandler._session.activateAdvertising();
        cdnHandler._session.setAdDataListener({
            onAdData: (adList) => {
                playerSSAIManager.processAdList(adList);
            },
        });
    },

    setupAdvertisingEvents(adsEvents: IAdEvents): void {
        if (!this.isSessionAlive(this._session)) return;

        this._session.setAdEventsListener({
            /**
             * Triggered when an ad break begin
             * @param adBreakData Ad break data
             */
            onAdBreakBegin: (adBreakData) => {
                // Fire ad break begin event
                npawPlugin?.getAdsAdapter()?.fireBreakStart(adBreakData);

                // Lock player controls
                adsEvents.onAdBreakBegin?.(adBreakData);
            },

            /**
             * Triggered when an ad begin
             * @param adData Ad data
             * @param adBreakData Ad break data
             */
            onAdBegin: (adData, adBreakData) => {
                // Add custom title = creativeId
                npawPlugin?.setAnalyticsOptions({
                    'ad.title': adData?.creativeId ?? 'NA',
                });

                // // Fire ad begin event
                npawPlugin?.getAdsAdapter()?.fireStart(adData, adBreakData);
                npawPlugin?.getAdsAdapter()?.fireJoin(adData, adBreakData);

                // Show ad link button if needed
                adsEvents.onAdBegin?.(adData, adBreakData);
            },

            /**
             * Triggered when an ad is skippable
             * @param adData Ad data
             * @param adBreakData Ad break data
             * @param adSkippablePosition position in millis where skip becomes allowed
             * @param adEndPosition position in millis of ad end
             * @param adBreakEndPosition position in millis of ad break end
             */
            onAdSkippable: (adData, adBreakData, adSkippablePosition, adEndPosition, adBreakEndPosition) => {
                // Show the skip message/button "skip ad in x seconds"
                adsEvents.onAdSkippable?.(adData, adBreakData, adSkippablePosition, adEndPosition, adBreakEndPosition);
            },

            /**
             * Triggered when the ad is ended, not called if skipped
             * @param adData Ad data
             * @param adBreakData Ad break data
             */
            onAdEnd: (adData, adBreakData) => {
                // Fire ad end event
                npawPlugin?.getAdsAdapter()?.fireStop(adData, adBreakData);

                // Hide ad link, hide ad skip button
                adsEvents.onAdEnd?.(adData, adBreakData);
            },

            /**
             * Triggered when ad break ended, even in case of skipping
             * @param adBreakData Ad break data
             */
            onAdBreakEnd: (adBreakData) => {
                // Fire ad break end event
                npawPlugin.getAdsAdapter()?.fireBreakStop(adBreakData);
                npawPlugin.getAdapter()?.fireJoin();

                // Unlock player controls, hide ad link, hide ad skip button
                adsEvents.onAdBreakEnd?.(adBreakData);
            },
        });
        this._session.setAdDataListener({
            /**
             * Triggered when ad data have been received during getURL
             * @param adList list of ad breaks
             */
            onAdData: (adList) => {
                adsEvents.onAdData?.(adList);
                void adList;
            },
        });
    },

    /**
     * Function to initialize SmartLib.
     * @param config
     */
    async init(config: any): Promise<void> {
        // await loadSmartLib();

        this.config = h.getCDNConfig(config);

        if (this._session) this.stopCDNSession();
        else {
            this._session = null;
            this._numberOfSessions = 0;
            this._player = null;
        }
        this._isAdsEnabled = false;

        if (this._initialized) {
            return;
        }
        if (!this.config) return;

        const userContext = user.getUserEnvironmentContext() || '';
        const { logLevel = SMARTLIB_LOG_LEVEL.DEBUG, umbrellaCDNHostName, override } = this.config || {};
        const { userEnvContexts, enable: overrideContext } = override || {};
        const overrideUserContext =
            userEnvContexts.find((item: string) => item.toLowerCase() === userContext.toLowerCase()) || '';
        if (overrideUserContext) {
            this.config.enabled = overrideContext;
        }
        if (!this.config.enabled) {
            return;
        }
        LoggerManager.getInstance().setLogLevel(logLevel);

        this.avodConfig = getAvodConfiguration();

        SmartLib.getInstance().init(
            this.avodConfig.analyticsAddress,
            '',
            umbrellaCDNHostName || 'uselector.cdn.intigral.net'
        );
        SmartLib.getInstance().setOption(
            StreamingSessionOptions.SESSION_REPORTING_MODE,
            StreamingSessionOptions.SESSION_REPORTING_MODE_METRICS_RECEIVER
        );
        this._initialized = true;
    },

    /**
     * Function to bind events to the player.
     */
    bindEvents(attachEvents: (events: PlayerEvent[], callback: AttachPlayerEventsCallback) => () => void): void {
        // TODO: not needed?
        const playerElm = player.getPlayer();
        if (!playerElm) return;

        this.destroyBoundEvents();
        this._destroy = attachEvents(
            [
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'playing',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'paused',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'seeked',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'timeshifted',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'seeking',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'playbackfinished',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'error',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'unload',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'destroy',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'sourceloaded',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'stallended',
            ],
            this._onPlaybackStateChanged.bind(this)
        );
    },

    /**
     * Function to destroy bound events.
     */
    destroyBoundEvents(): void {
        if (this._destroy) {
            this._destroy();
            this._destroy = null;
        }
    },

    /**
     * Function to start CDN session.
     */
    attachPlayerToCDNSession(playerInstance: PlayerAPI): void {
        if (!playerInstance || !isPlayerAlive(playerInstance)) return;
        if (!this.config?.enabled) return;

        this._player = playerInstance;

        if (this.isSessionAlive(this._session)) {
            this._session.attachPlayer(playerInstance);
        }
    },

    /**
     * Function to stop CDN session.
     */
    stopCDNSession(): void {
        const session = this._session;
        if (!this.config?.enabled || !session || session.stopped) return;

        try {
            session.stopStreamingSession();
        } catch (err) {
            // SmartLib detaches its listeners with `player.off(...)`; if the player was already
            // destroyed that is expected and harmless (guarded players never reach this branch).
            if (isApiNotAvailableError(err)) {
                markPlayerDestroyed(this._player);
            } else {
                console.error('[CDNHandler] - Error during SmartLib session stop:', err);
            }
        } finally {
            // Always clean up local state, even if stop fails
            this._session = null;
            this._player = null;
            this._isAdsEnabled = false;
            this._cdnAdsUrlParams = [];
            this._numberOfSessions = Math.max(0, this._numberOfSessions - 1);
        }
    },

    /**
     * Stops the CDN session only if it is bound to the given player (or to no player at all).
     * Meant to be called right before that player is destroyed, so SmartLib can detach its
     * listeners while the player API is still alive. A session that has already been re-bound
     * to a newer player instance is left untouched.
     */
    stopCDNSessionFor(playerInstance: PlayerAPI | null | undefined): void {
        if (!playerInstance) return;
        if (this._player && this._player !== playerInstance) return;

        this.stopCDNSession();
    },

    /**
     * Stops the CDN session when the player it is bound to is gone (destroyed or never attached).
     * Safe to call from a `PlayerEvent.Destroy` handler: if the session has meanwhile been bound to
     * a newer, live player instance (e.g. Live TV channel switch) it is kept.
     */
    stopCDNSessionIfPlayerGone(): void {
        if (this._player && isPlayerAlive(this._player)) return;

        this.stopCDNSession();
    },

    /**
     * Function to resume CDN session.
     */
    resumeCDNSession(): void {
        if (!this.config || !this.config.enabled) {
            return;
        }

        PlayerEventListener.notifyResume();
    },

    /**
     * Function to get CDN name from URL.
     * @param path
     * @returns {string}
     */
    getCDNName(path: string): string {
        if (this._cdnName) return this._cdnName;
        if (!path) return '';
        const subDomain = url.getSubDomain(path);
        if (this.isBroadPeakCDN(subDomain)) {
            return CDN.BROADPEAK;
        }
        return CDN.AKAMAI;
    },

    /**
     * Function to check if the CDN is BroadPeak.
     * @param subDomain
     * @returns {boolean}
     */
    isBroadPeakCDN(subDomain: string): boolean {
        const { broadpeakSubdomains } = this.config;
        if (!broadpeakSubdomains || !broadpeakSubdomains.length) return false;
        return broadpeakSubdomains.some((cdn: string) => subDomain.indexOf(cdn) > -1);
    },

    initiateSession() {
        // Close the previous session first (while its player is still alive) so it does not keep
        // sending keep-alives after being replaced.
        if (this.isSessionAlive(this._session)) this.stopCDNSession();

        // Create a new SmartLib session
        this._session = SmartLib.getInstance().createStreamingSession();
        const { keepaliveFrequency = 5000 } = this.config || {};
        SmartLib.getInstance().setOption(StreamingSessionOptions.SESSION_KEEPALIVE_FREQUENCY, keepaliveFrequency);
        this._numberOfSessions++;
    },

    /**
     * Resolves a URL using SmartLib
     * @param url The URL to resolve
     * @returns The resolved URL from SmartLib
     */
    async _resolveSmartLib(url: string, callback?: () => void): Promise<string> {
        // Configure SmartLib session
        // const { keepaliveFrequency = 5000 } = this.config || {};
        // // this.stopCDNSession();
        // SmartLib.getInstance().setOption(StreamingSessionOptions.SESSION_KEEPALIVE_FREQUENCY, keepaliveFrequency);

        // this.initiateSession();

        try {
            if (!this.isSessionAlive(this._session)) {
                throw new Error('[CDNHandler] SmartLib session is not alive');
            }

            // Get resolved URL
            const result = await this._session.getURL(url);
            if (!result.isError()) {
                return result.getURL();
            } else {
                // this._session.stopStreamingSession();
                this.stopCDNSession();
                throw new Error(`Failed to resolve URL with SmartLib for  ${url}`);
            }
        } catch (error) {
            if (this.isSessionAlive(this._session)) {
                // this._session.stopStreamingSession();
                this.stopCDNSession();
            }
            throw error;
        }
    },

    /**
     * Function to handle playback state changes.
     * @param prevEvent
     * @param currEvent
     * @param e
     * @param firstPlay
     */
    _onPlaybackStateChanged(prevEvent = '', currEvent: string, e: any, firstPlay: boolean): void {
        if (!this.isSessionAlive(this._session)) return;
        switch (currEvent) {
            case 'unload':
            case 'playbackfinished':
            case 'error':
            case 'destroy':
                this.stopCDNSession();
        }
    },
};

export default cdnHandler;
export type { IAdBreakData, IAdData };
export { retryN };
